/**
 * Hero rules — the single validation point for a whole Hero.
 *
 * A Hero's eyebrow, title and description must each say something and fit the
 * component's declared maximum. An empty value and a value of nothing but
 * spaces and line breaks are the same failure reached by different inputs, so
 * they share `trim()` and they share a message.
 *
 * Nothing is delegated here, because there is nothing to delegate to: a Hero's
 * three fields are plain words, so it inherits no supported-content model or
 * formatting rule. Length uses the shared scalar-slot validator. That is the
 * difference from
 * {@link validateTextHero}, which carries the same three field names but whose
 * description is a rich-text body and therefore borrows
 * `validateRichTextDocument`'s rules verbatim.
 *
 * Values are stored EXACTLY as entered, not trimmed. `trim()` decides whether a
 * value says anything; it does not get to edit what the Editor wrote.
 *
 * Check order is eyebrow → title → description, so an Editor who emptied two
 * fields at once is told about the first one rather than the last.
 *
 * Refusal is whole-payload atomic: the failing rule returns, nothing is
 * mutated, and no field is replaced.
 *
 * There is deliberately no rule about alignment and none about a call to
 * action. The component decides where its lockup sits, and a Hero has no call
 * to action — see the note in `blocks/hero.ts`.
 */
import { HERO_MAX_LENGTHS } from "./blocks/hero";
import { validateScalarSlotLength } from "./scalar-slot-validation";
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/**
 * Required and maximum-length rules per field, in the order they are checked.
 */
const FIELD_RULES = [
    ["eyebrow", "A hero needs an eyebrow", "Eyebrow", HERO_MAX_LENGTHS.eyebrow],
    ["title", "A hero needs a title", "Title", HERO_MAX_LENGTHS.heading],
    [
        "description",
        "A hero needs a description",
        "Description",
        HERO_MAX_LENGTHS.subheading,
    ],
];
/** Validate a whole Hero. */
export function validateHero(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Hero is malformed" };
    }
    for (const [key, reason, label, maxLength] of FIELD_RULES) {
        const field = value[key];
        if (typeof field !== "string" || field.trim() === "") {
            return { valid: false, reason };
        }
        const length = validateScalarSlotLength({ label, maxLength }, field);
        if (!length.valid)
            return length;
    }
    return {
        valid: true,
        hero: {
            eyebrow: value.eyebrow,
            title: value.title,
            description: value.description,
        },
    };
}
//# sourceMappingURL=hero.js.map